import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Flex, Spin } from "antd";
import { createStyles } from "antd-style";
import { isAxiosError } from "axios";

import {
  authCheck,
  generateMagicLink,
  loginWithMagicLink,
} from "@av/apps/common-apps/platform/services/authentication/authentication";
import { useFeatureFlagsSuspense } from "@av/apps/common-apps/platform/services/features/feature-flags";
import { App } from "@av/components/atoms/app/App";
import { Typography } from "@av/components/atoms/typography/Typography";
import { AvantiLogo } from "@av/components/molecules/logo/AvantiLogo";
import { demoLanguages, supportedLanguages } from "@av/config/i18n/locale-config";
import { getRetryAfterMinutes } from "@av/pages/auth/utils/retry-after";
import { useReturnTo } from "@av/pages/auth/utils/return-to";
import { useLocaleStore } from "@av/utils/hooks/useLocaleStore";
import { magicLinkRequestGuard } from "./api-call-cache";
import { MagicLinkConfirmFlow, type MagicLinkConfirmFlowState } from "./MagicLinkConfirmFlow";

const MAGIC_LINK_CONFIRMATION_FLAG = "platform_magic_link_confirmation";
// Links created before expiresIn was added need the previous default to remain usable.
const LEGACY_MAGIC_LINK_EXPIRY_MS = 5 * 60 * 1000;

const useStyles = createStyles(({ token, css }) => ({
  container: css`
    min-height: 100vh;
    width: 100vw;
    background: ${token.colorBgContainer};
  `,
  errorMessage: css`
    margin-bottom: ${token.marginXL}px;
    text-align: center;
    color: ${token.colorTextSecondary};
  `,
  loadingText: css`
    margin-top: ${token.marginLG}px;
  `,
}));

// Mirrors the locales SmtpAuthEmailGateway supports (api/.../adapters/SmtpAuthEmailGateway.java's
// EMAIL_TEMPLATES); the two must stay in sync manually.
const KNOWN_MAGIC_LINK_LOCALES = new Set<string>(
  [...supportedLanguages, ...demoLanguages].map((language) => language.value)
);

function isKnownMagicLinkLocale(locale: string | null): locale is string {
  return locale !== null && KNOWN_MAGIC_LINK_LOCALES.has(locale);
}

function readMagicLinkFragment() {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const sentAt = params.get("sentAt");
  const expiresIn = Number(params.get("expiresIn"));
  const locale = params.get("locale");

  return {
    email: params.get("email"),
    sentAtMs: sentAt ? Number(sentAt) : null,
    expiresIn:
      Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : LEGACY_MAGIC_LINK_EXPIRY_MS,
    locale: isKnownMagicLinkLocale(locale) ? locale : null,
  };
}

function isExpiredByClock(sentAtMs: number | null, expiresIn: number) {
  return sentAtMs !== null && Date.now() - sentAtMs > expiresIn;
}

// Do not switch this to useBooleanFlagValue()/OpenFeature: OpenFeature.setProvider(...) only
// runs one render after mount, so it reads the fallback (false) on the very first render and
// can fire the legacy auto-login path even when the flag is on. This reads the suspense-resolved
// flags object directly, which is correct synchronously on the first render.
function isFlagOn(
  flags: Record<string, { variants: Record<string, boolean>; defaultVariant: string }>,
  key: string
) {
  const flag = flags[key];

  return flag ? flag.variants[flag.defaultVariant] === true : false;
}

export function MagicLinkLandingPage() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { locale } = useLocaleStore();
  const { styles } = useStyles();
  const token = searchParams.get("token");
  const returnTo = useReturnTo();
  const [hasError, setHasError] = useState(false);
  const { data: flags } = useFeatureFlagsSuspense();
  const isConfirmationEnabled = isFlagOn(flags, MAGIC_LINK_CONFIRMATION_FLAG);
  const [{ email, sentAtMs, expiresIn, locale: originalLocale }] = useState(readMagicLinkFragment);
  const [confirmState, setConfirmState] = useState<MagicLinkConfirmFlowState>(() =>
    isExpiredByClock(sentAtMs, expiresIn) ? "expired" : "confirm"
  );
  const [retryAfterMinutes, setRetryAfterMinutes] = useState<number>();
  const [existingSessionChecked, setExistingSessionChecked] = useState(false);
  const [isRequestingNewLink, setIsRequestingNewLink] = useState(false);

  if (!token) {
    navigate("/not_found");
  }

  // useCallback is required here (not optional): this is a dependency of the auto-login effect
  // below, and an unmemoized reference would re-run that effect on every render.
  const loginWithToken = useCallback(async () => {
    if (!token || magicLinkRequestGuard.wasAlreadyCalled()) {
      return;
    }

    magicLinkRequestGuard.markAsCalled();

    try {
      const success = await loginWithMagicLink(token);

      if (!success) {
        throw new Error("Magic link authentication failed");
      }

      // Feature flags fetched on this page are the signed-out system values, so the signed-in app
      // must fetch them again for the session's company.
      queryClient.getQueryCache().clear();
      navigate(returnTo ?? "/");
    } catch (err) {
      console.error("Auth request failed", err);

      // 429 is the edge WAF rate limit (infra/cdk/lib/waf-stack.ts), not a bad link. The link
      // is still valid, so show the wait time and let the user retry instead of "invalid link".
      // A link that already expired by the clock is still reported as expired below.
      if (isAxiosError(err) && err.status === 429 && !isExpiredByClock(sentAtMs, expiresIn)) {
        setRetryAfterMinutes(getRetryAfterMinutes(err));
        setConfirmState("rateLimited");

        return;
      }

      setHasError(true);
      // Reclassify as expired if the clock crossed the TTL between mount and this failure.
      setConfirmState(isExpiredByClock(sentAtMs, expiresIn) ? "expired" : "error");
    }
  }, [navigate, queryClient, token, returnTo, sentAtMs, expiresIn]);

  // A link fresh at mount can still expire while the confirm screen sits open; arm a timer for
  // the remaining TTL so the card flips to "expired" on its own instead of leaving a Continue
  // button that's guaranteed to fail.
  useEffect(() => {
    if (!isConfirmationEnabled || confirmState !== "confirm" || sentAtMs === null) {
      return;
    }

    const remainingMs = sentAtMs + expiresIn - Date.now();

    if (remainingMs <= 0) {
      setConfirmState("expired");

      return;
    }

    const timeoutId = setTimeout(() => setConfirmState("expired"), remainingMs);

    return () => clearTimeout(timeoutId);
  }, [isConfirmationEnabled, confirmState, sentAtMs, expiresIn]);

  useEffect(() => {
    if (isConfirmationEnabled) {
      return;
    }

    loginWithToken();
  }, [isConfirmationEnabled, loginWithToken]);

  useEffect(() => {
    if (!isConfirmationEnabled || !token) {
      return;
    }

    const controller = new AbortController();

    authCheck(controller)
      .then(() => navigate(returnTo ?? "/"))
      .catch(() => setExistingSessionChecked(true));

    return () => controller.abort();
  }, [isConfirmationEnabled, token, returnTo, navigate]);

  // Not wrapped in useCallback: MagicLinkConfirmFlow isn't memoized and no effect depends on
  // this reference, so memoizing it would just add overhead without a measurable benefit.
  const handleContinue = () => {
    if (isExpiredByClock(sentAtMs, expiresIn)) {
      setConfirmState("expired");

      return;
    }

    setConfirmState("loggingIn");
    magicLinkRequestGuard.clear();
    loginWithToken();
  };

  const handleRequestNewLink = async () => {
    if (!email) {
      // The confirm-flow email comes from the magic-link fragment; if it's missing (e.g. an
      // old link generated before this flow existed), there's no address to resend to - point
      // the user back to the login page instead of leaving this button a silent no-op.
      message.error(t("auth.login.magicLinkForm_error"));
      navigate("/login");

      return;
    }

    if (isRequestingNewLink) {
      return;
    }

    setIsRequestingNewLink(true);

    try {
      const success = await generateMagicLink(email, originalLocale || locale, returnTo);

      if (!success) {
        throw new Error("Magic link generation failed");
      }

      message.success(t("auth.login.magicLinkForm_success"));
    } catch (err) {
      console.error("Failed to request a new magic link", err);
      message.error(t("auth.login.magicLinkForm_error"));
    } finally {
      setIsRequestingNewLink(false);
    }
  };

  if (isConfirmationEnabled) {
    if (!existingSessionChecked) {
      return (
        <Flex vertical align="center" justify="center" className={styles.container}>
          <Spin size="large" />
        </Flex>
      );
    }

    return (
      <Flex vertical align="center" justify="center" className={styles.container}>
        <AvantiLogo size="large" testIdPrefix="magic-link-landing" />
        <MagicLinkConfirmFlow
          state={confirmState}
          email={email}
          expiresIn={expiresIn}
          retryAfterMinutes={retryAfterMinutes}
          onContinue={handleContinue}
          onRequestNewLink={handleRequestNewLink}
          isRequestingNewLink={isRequestingNewLink}
        />
      </Flex>
    );
  }

  return (
    <Flex vertical align="center" justify="center" className={styles.container}>
      <AvantiLogo size="large" testIdPrefix="magic-link-landing" />

      {confirmState === "rateLimited" ? (
        <>
          <Typography.Title testIdPrefix="magic-link-landing-rate-limited" level={3}>
            {t("auth.login.magicLinkLanding_rateLimitedTitle")}
          </Typography.Title>

          <Typography.Paragraph
            testIdPrefix="magic-link-landing-rate-limited"
            className={styles.errorMessage}
          >
            {t("auth.login.common_tooManyAttempts", { count: retryAfterMinutes })}
          </Typography.Paragraph>
        </>
      ) : hasError ? (
        <>
          <Typography.Title testIdPrefix="magic-link-landing-invalid-link" level={3}>
            {t("auth.login.magicLinkLanding_invalidTitle")}
          </Typography.Title>

          <Typography.Paragraph
            testIdPrefix="magic-link-landing-invalid-link"
            className={styles.errorMessage}
          >
            {t("auth.login.magicLinkLanding_invalidMessage", {
              count: Math.ceil(expiresIn / 60_000),
            })}
          </Typography.Paragraph>
        </>
      ) : (
        <>
          <Spin size="large" />
          <Typography.Title
            testIdPrefix="magic-link-landing-valid-link"
            level={4}
            type="secondary"
            className={styles.loadingText}
          >
            {t("auth.login.magicLinkLanding_loggingYouIn")}
          </Typography.Title>
        </>
      )}
    </Flex>
  );
}
